import type { ReactNode } from "react";

/**
 * A card-suit pip drawn as INLINE SVG.
 *
 * ⚠️ DO NOT go back to the Unicode characters ♠♥♦♣ (U+2660/2665/2666/2663). They are bare
 * TEXT-presentation codepoints and **no font this app ships can render them**: Vazirmatn
 * carries zero suit glyphs in all three of its subsets, and its `unicode-range`
 * declarations do not even cover that block — so the browser never consults it and the
 * character resolves entirely against the DEVICE's own fonts. On a device whose fallback
 * inventory has no text-presentation suit (reported from prod on android, 2026-08-22) the
 * «حکم» badge painted an empty disc. Card FACES were unaffected because they are
 * `@letele/playing-cards` SVG — which is why a player could see diamonds in play beside a
 * blank trump badge.
 *
 * Appending VS16 for emoji presentation is NOT the fix either: that hands the glyph to a
 * COLOR font, where `color:` no longer applies and the red/black suit distinction is lost.
 *
 * The artwork is inset inside the 24×24 box (roughly y 3.2‥20.8, ~0.72 em) so it lands at
 * the same optical size a text glyph had — every call site keeps its existing `font-size`
 * and the layout does not move. Paints with `currentColor`, so the callers' colour tokens
 * (`--c-suit-red-on-gold` / `--c-ink-on-gold` / `text-danger-bluff` / `text-suit-ink`)
 * keep working exactly as before.
 */
const SUIT_ART: Record<string, ReactNode> = {
  spades: (
    <path d="M12 3.2s-8 6.4-8 10.9c0 2.3 1.8 4.1 4 4.1 1.6 0 3-.9 3.6-2.2-.2 1.9-.8 3.6-2 4.8h4.8c-1.2-1.2-1.8-2.9-2-4.8.6 1.3 2 2.2 3.6 2.2 2.2 0 4-1.8 4-4.1C20 9.6 12 3.2 12 3.2z" />
  ),
  hearts: (
    <path d="M12 20.6S4 15.1 4 9.9C4 7.2 6.1 5.2 8.7 5.2c1.5 0 2.7.7 3.3 1.7.6-1 1.8-1.7 3.3-1.7C17.9 5.2 20 7.2 20 9.9c0 5.2-8 10.7-8 10.7z" />
  ),
  diamonds: <path d="M12 3.2 19.2 12 12 20.8 4.8 12z" />,
  clubs: (
    <>
      <circle cx="12" cy="7.6" r="3.7" />
      <circle cx="7.3" cy="14.1" r="3.7" />
      <circle cx="16.7" cy="14.1" r="3.7" />
      <path d="M9.6 20.8c1.4-1.4 2-3.2 2.2-5.4h.4c.2 2.2.8 4 2.2 5.4z" />
    </>
  ),
};

/**
 * `suit` is the full word the engines use ("spades"…). Unknown — or a not-yet-chosen
 * trump, which the engines type as `string | null` — renders nothing.
 */
export function SuitGlyph({
  suit,
  size = "1em",
}: {
  suit: string | null | undefined;
  size?: number | string;
}): ReactNode {
  const art = suit ? SUIT_ART[suit] : undefined;
  if (!art) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      {art}
    </svg>
  );
}
